export type ReplyNotification = {
  id: string
  created_at: string
  read_at: string | null
  post_id: string
  comment_id: string
  actor: { id: string; display_name: string; handle: string; avatar_url: string | null; avatar_seed: string | null }
  post_preview: string
  reply_preview: string
}

export type ReplyNotificationPage = {
  viewer_member_id: string
  notifications: ReplyNotification[]
  unread_count: number
  has_more: boolean
  next_cursor: string | null
}

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$/
const CURSOR_PATTERN = /^[A-Za-z0-9_-]{1,1024}$/
export const NOTIFICATION_POLL_MS = 30_000

export class ReplyNotificationError extends Error {
  readonly status: number
  readonly accountChanged: boolean
  constructor(message: string, status = 0, accountChanged = false) { super(message); this.name = 'ReplyNotificationError'; this.status = status; this.accountChanged = accountChanged }
}

function invalidResponse(): never { throw new ReplyNotificationError('Notifications could not be verified. Try refreshing.') }
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) invalidResponse()
  return value as Record<string, unknown>
}
function validDate(value: unknown): value is string { return typeof value === 'string' && Number.isFinite(Date.parse(value)) && new Date(value).toISOString() === value }
function validId(value: unknown): value is string { return typeof value === 'string' && ID_PATTERN.test(value) }
function validCount(value: unknown): value is number { return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 }

export function notificationBadge(count: number): string { return count > 99 ? '99+' : String(Math.max(0, count)) }
export function notificationLabel(count: number): string { return count > 0 ? `Notifications, ${count} unread` : 'Notifications' }

export function parseNotificationPage(value: unknown): ReplyNotificationPage {
  const payload = record(value)
  if (!validId(payload.viewer_member_id) || !Array.isArray(payload.notifications) || !validCount(payload.unread_count) || typeof payload.has_more !== 'boolean'
    || (payload.next_cursor !== null && (typeof payload.next_cursor !== 'string' || !CURSOR_PATTERN.test(payload.next_cursor)))) invalidResponse()
  if (payload.notifications.length > 50 || (payload.has_more && (!payload.next_cursor || !payload.notifications.length)) || (!payload.has_more && payload.next_cursor !== null)) invalidResponse()
  const notifications = payload.notifications.map((value): ReplyNotification => {
    const row = record(value), actor = record(row.actor)
    if (!validId(row.id) || !validId(row.post_id) || !validId(row.comment_id) || !validId(actor.id)
      || !validDate(row.created_at) || (row.read_at !== null && !validDate(row.read_at))
      || typeof actor.display_name !== 'string' || typeof actor.handle !== 'string'
      || typeof row.post_preview !== 'string' || typeof row.reply_preview !== 'string'
      || (actor.avatar_seed !== null && typeof actor.avatar_seed !== 'string')
      || (actor.avatar_url !== null && (typeof actor.avatar_url !== 'string' || !actor.avatar_url.startsWith('/api/avatars/')))) invalidResponse()
    return {
      id: row.id, post_id: row.post_id, comment_id: row.comment_id, created_at: row.created_at, read_at: row.read_at,
      actor: { id: actor.id, display_name: actor.display_name, handle: actor.handle, avatar_url: actor.avatar_url, avatar_seed: actor.avatar_seed },
      post_preview: row.post_preview, reply_preview: row.reply_preview,
    }
  })
  if (new Set(notifications.map((item) => item.id)).size !== notifications.length) invalidResponse()
  return { viewer_member_id: payload.viewer_member_id, notifications, unread_count: payload.unread_count, has_more: payload.has_more, next_cursor: payload.next_cursor }
}

async function responsePayload(response: Response): Promise<unknown> {
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    const error = payload && typeof payload === 'object' ? payload as Record<string, unknown> : null
    throw new ReplyNotificationError(typeof error?.message === 'string' ? error.message : 'Notifications are temporarily unavailable.', response.status, error?.error === 'account_changed' || response.status === 401)
  }
  return payload
}

export async function listReplyNotifications(
  options: { before?: string | null; summary?: boolean; signal?: AbortSignal; expectedMemberId?: string } = {}, fetcher: typeof fetch = fetch,
): Promise<ReplyNotificationPage> {
  const params = new URLSearchParams({ limit: '20' })
  if (options.before) {
    if (!CURSOR_PATTERN.test(options.before)) throw new ReplyNotificationError('The notification page is invalid.')
    params.set('before', options.before)
  }
  if (options.summary) params.set('summary', '1')
  const page = parseNotificationPage(await responsePayload(await fetcher(`/api/notifications?${params}`, {
    credentials: 'same-origin', cache: 'no-store', signal: options.signal,
    headers: options.expectedMemberId ? {'x-journey90-viewer-id': options.expectedMemberId} : undefined,
  })))
  if (options.expectedMemberId && page.viewer_member_id !== options.expectedMemberId) throw new ReplyNotificationError('Your account changed. Refresh Journey90 to continue.', 409, true)
  return page
}

export async function markReplyNotificationsRead(
  selection: { ids: string[] } | { all: true }, signal?: AbortSignal, fetcher: typeof fetch = fetch, expectedMemberId?: string,
): Promise<{ viewer_member_id: string; unread_count: number; marked_read: number }> {
  if ('ids' in selection && (!Array.isArray(selection.ids) || !selection.ids.length || selection.ids.length > 50 || !selection.ids.every(validId))) {
    throw new ReplyNotificationError('The selected notifications are invalid.')
  }
  if (!('ids' in selection) && selection.all !== true) throw new ReplyNotificationError('The selected notifications are invalid.')
  const payload = record(await responsePayload(await fetcher('/api/notifications', {
    method: 'PATCH', credentials: 'same-origin', cache: 'no-store', signal,
    headers: { 'content-type': 'application/json', ...(expectedMemberId ? {'x-journey90-viewer-id': expectedMemberId} : {}) }, body: JSON.stringify(selection),
  })))
  if (!validId(payload.viewer_member_id) || !validCount(payload.unread_count) || !validCount(payload.marked_read)) invalidResponse()
  if (expectedMemberId && payload.viewer_member_id !== expectedMemberId) throw new ReplyNotificationError('Your account changed. Refresh Journey90 to continue.', 409, true)
  return { viewer_member_id: payload.viewer_member_id, unread_count: payload.unread_count, marked_read: payload.marked_read }
}
